import type { MutableRefObject } from 'react';
import { useEffect } from 'react';
import type { WidgetBackgroundProps } from './types';

const ZOOM_INDEX = 20;
const POSITION_SHIFT_INDEX = 2;

const listOfTriggeredAnimations: Required<
  WidgetBackgroundProps['imageAnimationEffect']
>[] = ['zoom'];

type UseImageAnimationProps = {
  enabled: boolean;
  imageAnimationEffect: WidgetBackgroundProps['imageAnimationEffect'];
  triggerImageAnimation: boolean;
  backgroundRef: MutableRefObject<HTMLDivElement | null>;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  contentRef: MutableRefObject<HTMLDivElement | null>;
};

export function useImageAnimation({
  enabled,
  imageAnimationEffect = 'none',
  triggerImageAnimation,
  backgroundRef,
  containerRef,
  contentRef
}: UseImageAnimationProps) {
  const clearedBackgroundStyles = {
    transition: 'none',
    transform: ''
  };

  const backgroundZoomStyles = {
    transition: 'transform 20s ease'
  };

  const backgroundParallaxStyles = {
    transform: `scale(${1 + ZOOM_INDEX / 100})`
  };

  const backgroundAnimationStyles = {
    none: clearedBackgroundStyles,
    zoom: backgroundZoomStyles,
    parallax: backgroundParallaxStyles
  }[imageAnimationEffect];

  useEffect(() => {
    if (!enabled || imageAnimationEffect === 'none') {
      return;
    }

    // zoom
    if (imageAnimationEffect === 'zoom') {
      const { current: background } = backgroundRef;

      if (background) {
        background.style.transition = 'none';
        background.style.transform = '';

        // unapply zoom styles
        if (!triggerImageAnimation) {
          return;
        }

        setTimeout(() => {
          background.style.transition = backgroundZoomStyles.transition;
          background.style.transform = `scale(${1 + ZOOM_INDEX / 100})`;
        });
      }

      return;
    }

    // parallax
    const handler = (event: MouseEvent) => {
      const { current: background } = backgroundRef;
      const { current: container } = containerRef;
      const { current: content } = contentRef;

      if (!container || !background || !content) {
        return;
      }

      const clientX = event.clientX;
      const clientY = event.clientY;

      const parallaxLeftOffset = container.getBoundingClientRect().left;
      const parallaxTopOffset = container.getBoundingClientRect().top;

      const x = clientX - parallaxLeftOffset - 0.5 * container.offsetWidth;
      const y = clientY - parallaxTopOffset - 0.5 * container.offsetHeight;

      background.style.transition = 'transform 0.3s linear';
      background.style.transform = `${
        backgroundParallaxStyles.transform
      } translate(${-((x * POSITION_SHIFT_INDEX) / 100).toFixed(2)}px, ${-(
        (y * POSITION_SHIFT_INDEX) /
        100
      ).toFixed(2)}px)`;

      content.style.transform = `translate(${(
        (x * POSITION_SHIFT_INDEX) /
        100
      ).toFixed(2)}px, ${((y * POSITION_SHIFT_INDEX) / 100).toFixed(2)}px)`;
    };

    if (imageAnimationEffect === 'parallax') {
      containerRef.current?.addEventListener('mousemove', handler);
    }

    return () =>
      containerRef.current?.removeEventListener('mousemove', handler);
  }, [enabled, imageAnimationEffect, triggerImageAnimation]);

  useEffect(() => {
    if (!enabled || imageAnimationEffect !== 'parallax') {
      return;
    }

    const resetParallax = () => {
      const { current: background } = backgroundRef;
      const { current: content } = contentRef;

      if (!background || !content) {
        return;
      }

      background.style.transform = backgroundParallaxStyles.transform;
      content.style.transform = '';
    };

    containerRef.current?.addEventListener('mouseout', resetParallax);

    return () =>
      containerRef.current?.removeEventListener('mouseout', resetParallax);
  }, [enabled, imageAnimationEffect]);

  if (
    !triggerImageAnimation &&
    listOfTriggeredAnimations.includes(imageAnimationEffect)
  ) {
    return clearedBackgroundStyles;
  }

  return backgroundAnimationStyles;
}
